/* eslint-disable class-methods-use-this */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { compose } from 'ramda';
import { connect } from 'react-redux';
import ui from 'redux-fui';
import { hot } from 'react-hot-loader';
import Waypoint from 'react-waypoint';
import { withRouter } from 'react-router-dom';
import LegacyLink from '../components/Common/LegacyLink';
import HomePhoneWebp from '../../static/images/home_phone.webp';
import HomePhonePng from '../../static/images/home_phone.png';
import ConnectedSearchForm from '../components/Search/Form/SearchForm';
import ThreeFeatureSlider from '../components/Common/ThreeFeatureSlider';
import AppFeature from '../components/Common/AppFeature';
import ContentModule from '../components/Common/ContentModule';
import Media from '../components/Common/Media';
import withAnalytics from '../components/Decorators/withAnalytics';
import ReviewsPanel from '../components/Common/ReviewsPanel';
import PMEMap from '../../static/images/pme_map.png';
import PMEPark from '../../static/images/pme_park.png';
import PMEReserve from '../../static/images/pme_reserve.png';
import * as actions from '../stores/search';

export class Home extends Component {
  constructor(props) {
    super(props);

    this.sliderSettings = {
      dots: true,
      arrows: false,
      responsive: [
        {
          breakpoint: 640,
          settings: { touchThreshold: 2, slidesToShow: 1, infinite: false }
        },
        { breakpoint: 100000, settings: 'unslick' }
      ]
    };
  }

  componentDidMount() {
    this.props.analytics.page('homepage');
  }

  render() {
    return (
      <div className="p-home">
        <Helmet
          title="JustPark - The Parking App | Find parking in seconds"
          titleTemplate="%s"
        >
          <link rel="canonical" href="https://www.justpark.com/" />
        </Helmet>
        <Waypoint
          onEnter={() => {
            this.props.updateUI({ headerShouldShowCTA: false });
          }}
          onLeave={() => {
            this.props.updateUI({ headerShouldShowCTA: true });
          }}
        >
          <ContentModule baseClassName="hero" modifier="home">
            <div className="c-hero-content">
              <div className="c-hero-content__clear">
                <div className="c-hero__phone">
                  <picture>
                    <source srcSet={HomePhoneWebp} type="image/webp" />
                    <source srcSet={HomePhonePng} type="image/png" />
                    <img
                      className="c-hero__phone-img"
                      src={HomePhonePng}
                      alt="Mobile Phone"
                    />
                  </picture>
                </div>
                <div className="c-hero-content__inner">
                  <h1>
                    Find parking in <span>seconds</span>
                  </h1>
                  <p className="mobile-hidden">
                    Choose from millions of available spaces, or reserve your
                    space in advance. Join over 1.5 million drivers enjoying
                    easy parking.
                  </p>
                  <p className="desktop-hidden">
                    Choose from millions of spaces. <br />
                    Trusted by 1.5 million drivers.
                  </p>
                  <ConnectedSearchForm
                    placeholder="Where do you want to park?"
                    modifier="single"
                    cta="Show parking spaces"
                    highlight={this.props.ui.animateSearchForm}
                    location={this.props.location}
                    allowScroll
                    noSubmit
                    disableDefaultDates
                    onSubmitCallback={query => {
                      this.props.setSearchInitialiser(
                        'autosuggest_searched',
                        query.q
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </ContentModule>
        </Waypoint>
        <ThreeFeatureSlider
          modifier="parking-made-easy"
          callback={() => {
            this.props.updateUI({ animateSearchForm: true });
            if (__CLIENT__) {
              window.scroll(0, 0);
            }
          }}
        >
          <Media modifier="stacked" title="Wherever, whenever" image={PMEMap}>
            <ul>
              <li>
                <span>Choose from millions of spaces across the UK</span>
              </li>
              <li>
                <span>Find your best option for every car journey</span>
              </li>
            </ul>
          </Media>
          <Media modifier="stacked" title="Peace of mind" image={PMEReserve}>
            <ul>
              <li>
                <span>
                  View information on availability, price and restrictions
                </span>
              </li>
              <li>
                <span>Reserve in advance at over 20,000+ locations</span>
              </li>
            </ul>
          </Media>
          <Media modifier="stacked" title="Seamless experience" image={PMEPark}>
            <ul>
              <li>
                <span>Pay for JustPark spaces via the app or website</span>
              </li>
              <li>
                <span>Follow easy directions and access instructions</span>
              </li>
            </ul>
          </Media>
        </ThreeFeatureSlider>
        <AppFeature />
        <ContentModule baseClassName="hero" modifier="rent-space">
          <div className="c-hero-content">
            <div className="c-hero-content__clear">
              <div className="c-hero-content__inner">
                <h2>Rent out your parking space</h2>
                <p>
                  Make easy money by renting out your parking space. It&lsquo;s
                  free to list and only takes a few minutes to get up and
                  running.
                </p>
                <LegacyLink
                  className="bt--primary"
                  to="rent-out-a-parking-space/"
                >
                  Learn how to earn today
                </LegacyLink>
              </div>
            </div>
          </div>
        </ContentModule>
        <ReviewsPanel modifier="home" />
        <ContentModule baseClassName="hero" modifier="parking-management">
          <div className="c-hero-content">
            <div className="c-hero-content__clear">
              <div className="c-hero-content__inner">
                <h2>Car park management</h2>
                <p>
                  Maximise yield from underused car parks and vacant land, or
                  transform payments with the UK’s favourite parking app.
                </p>
                <LegacyLink className="bt--primary" to="car-park-management/">
                  Learn about our solutions
                </LegacyLink>
              </div>
            </div>
          </div>
        </ContentModule>
      </div>
    );
  }
}

Home.propTypes = {
  analytics: PropTypes.object.isRequired,
  ui: PropTypes.object,
  updateUI: PropTypes.func,
  location: PropTypes.object.isRequired,
  setSearchInitialiser: PropTypes.func.isRequired
};

Home.defaultProps = {
  ui: {},
  updateUI: () => {}
};

const mapDispatchToProps = {
  setSearchInitialiser: actions.setSearchInitialiser
};

const homeComposition = compose(
  withRouter,
  connect(
    () => ({}),
    mapDispatchToProps
  ),
  withAnalytics({}, {}),
  ui({ key: 'home' })
)(Home);

export default hot(module)(homeComposition);
